import { blo } from 'blo'
import { Avatar, AvatarFallback, AvatarImage } from '@safe-global/views/components/ui/avatar'
import { Typography } from '@safe-global/views/components/ui/typography'
import { useSafeDisplayName } from '@/hooks/useSafeDisplayName'
import SafeBalanceBlock from './SafeBalanceBlock'
import { ThresholdBadge } from '@safe-global/views/components/common/AccountBadges'
import {
  CopyAddressButton,
  ExplorerLinkButton,
  FullAddress,
  TruncatedText,
  getInitials,
  getSafeDisplayInfo,
} from '@/components/common/AccountRow'
import NotActivatedBadge from '@safe-global/views/components/common/NotActivatedBadge'
import type { SafeItemData } from '@safe-global/views/features/spaces/components/SafeSelectorDropdown/types'
import EnvHintButton from '@/components/settings/EnvironmentVariables/EnvHintButton'
import { useChain } from '@/hooks/useChains'
import { getBlockExplorerLink } from '@safe-global/utils/utils/chains'
import { HypernativeFeature, useIsHypernativeGuard } from '@/features/hypernative'
import { useLoadFeature } from '@/features/__core__'

export interface SafeSelectorTriggerContentProps {
  selectedItem: SafeItemData
  selectedChainId: string
}

function SafeSelectorTriggerContent({ selectedItem, selectedChainId }: SafeSelectorTriggerContentProps) {
  const selectedChain = selectedItem.chains.find((c) => c.chainId === selectedChainId) ?? selectedItem.chains[0]
  const isUndeployed = Boolean(selectedChain?.isUndeployed)
  const isActivating = Boolean(selectedChain?.isActivating)

  const resolvedName = useSafeDisplayName(selectedItem.address, selectedChainId)
  const { shortAddress, displayName } = getSafeDisplayInfo(resolvedName, selectedItem.address)

  const chainConfig = useChain(selectedChain?.chainId ?? '')
  const blockExplorerLink = chainConfig ? getBlockExplorerLink(chainConfig, selectedItem.address) : undefined

  const { SafeHeaderHnTooltip } = useLoadFeature(HypernativeFeature)
  const { isHypernativeGuard } = useIsHypernativeGuard()

  return (
    <div className="flex items-center gap-2 w-full" data-testid="safe-header-info">
      <div className="relative shrink-0">
        <Avatar size="sm" data-testid="safe-icon">
          <AvatarImage src={blo(selectedItem.address as `0x${string}`)} alt={displayName} />
          <AvatarFallback>{getInitials(displayName || '?')}</AvatarFallback>
        </Avatar>
      </div>
      <div className="flex flex-col items-start flex-1 min-w-0" data-testid="safe-selector-trigger-details">
        <div className="flex items-center gap-1 min-w-0 max-w-full">
          <TruncatedText
            data-testid="safe-selector-trigger-name"
            variant="paragraph-small-medium"
            className="block min-w-0"
            text={displayName}
          />
          {isHypernativeGuard && <SafeHeaderHnTooltip />}
        </div>
        <div className="flex items-center gap-1 min-w-0 max-w-full">
          <FullAddress
            address={selectedItem.address}
            className="max-sm:hidden"
            data-testid="safe-selector-trigger-address"
          />
          {/* The full address would starve the name/balance on small screens — short form instead. */}
          <Typography variant="paragraph-mini" color="muted" className="font-mono sm:hidden">
            {shortAddress}
          </Typography>
          {/* Inline after the address so hovering never paints over it — the actions reserve their
              width and the address middle-truncates when space is short. Hidden (not removed) until
              hover/focus on sm+ so the layout stays stable; always visible inline on touch. */}
          <span className="flex shrink-0 items-center gap-0.5 sm:pointer-events-none sm:opacity-0 sm:transition-opacity sm:group-hover:pointer-events-auto sm:group-hover:opacity-100 sm:group-focus-within:pointer-events-auto sm:group-focus-within:opacity-100">
            <CopyAddressButton address={selectedItem.address} />
            {blockExplorerLink && <ExplorerLinkButton href={blockExplorerLink.href} title={blockExplorerLink.title} />}
            <EnvHintButton chainId={selectedChainId} />
          </span>
        </div>
      </div>
      {selectedItem.owners > 0 && (
        // flex (not inline): the inline-flex badge would otherwise sit on the wrapper's text
        // baseline and render a couple of px above the vertical middle of the chip.
        <span className="flex shrink-0 items-center max-sm:hidden">
          {/* The trigger always reflects the active safe on the active chain, so it shows that chain's
              threshold — even for a multi-chain safe (the dropdown group summary stays icon-only). */}
          <ThresholdBadge threshold={selectedItem.threshold} owners={selectedItem.owners} />
        </span>
      )}
      {isUndeployed ? (
        <NotActivatedBadge isActivating={isActivating} data-testid="safe-selector-not-activated-icon" />
      ) : (
        <SafeBalanceBlock isLoading={selectedItem.isLoading ?? false} balance={selectedItem.balance} />
      )}
    </div>
  )
}

export default SafeSelectorTriggerContent
